import type { Organization } from "@pex/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../types";

function schemaClient(client: SupabaseClient<Database>, schema: string) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema(schema);
}

function orgs(client: SupabaseClient<Database>) {
  return schemaClient(client, "shared").from("organizations");
}

// The columns that make up the Organization domain type. Projected explicitly
// instead of select("*") so these queries never over-fetch organization columns
// the caller does not consume (e.g. logo_url, address fields, account_tier) —
// see #102.
const ORGANIZATION_COLUMNS = "id, name, type, created_at, updated_at";

// The billing portal is restricted to account managers (see
// docs/reference/rbac-reference.md — "Bridge | account_manager role only").
const BILLING_ROLE = "account_manager";

/**
 * Resolve the organisation IDs a user has an active facilitator membership in,
 * optionally restricted to a specific membership role.
 *
 * Returns an empty array when the user has no facilitator record or no matching
 * memberships. Mirrors the three-step bridge resolution (account → facilitator
 * → organization_facilitators).
 */
async function getActiveMembershipOrgIds(
  client: SupabaseClient<Database>,
  userId: string,
  role?: string
): Promise<string[]> {
  // Step 1: resolve the user's facilitator record
  const { data: facilitator, error: facError } = await schemaClient(
    client,
    "bridge"
  )
    .from("facilitators")
    .select("id")
    .eq("account_id", userId)
    .maybeSingle();

  if (facError) {
    throw new Error(`Failed to fetch facilitator record: ${facError.message}`);
  }

  if (!facilitator) {
    return [];
  }

  // Step 2: get org IDs for active memberships (optionally role-scoped)
  let query = schemaClient(client, "bridge")
    .from("organization_facilitators")
    .select("organization_id")
    .eq("facilitator_id", facilitator.id)
    .eq("status", "active");

  if (role) {
    query = query.eq("role", role);
  }

  const { data: memberships, error: memError } = await query;

  if (memError) {
    throw new Error(`Failed to fetch org memberships: ${memError.message}`);
  }

  return (
    (memberships as Array<{ organization_id: string }> | null)?.map(
      (m) => m.organization_id
    ) ?? []
  );
}

async function fetchOrganizationsByIds(
  client: SupabaseClient<Database>,
  orgIds: string[]
): Promise<Organization[]> {
  if (orgIds.length === 0) {
    return [];
  }

  const { data, error } = await orgs(client)
    .select(ORGANIZATION_COLUMNS)
    .in("id", orgIds);

  if (error) {
    throw new Error(`Failed to fetch organizations: ${error.message}`);
  }

  return data as Organization[];
}

export async function getUserOrganizations(
  client: SupabaseClient<Database>,
  userId: string,
  appContext?: string
): Promise<Organization[]> {
  if (appContext === "bridge" || !appContext) {
    const orgIds = await getActiveMembershipOrgIds(client, userId);
    return fetchOrganizationsByIds(client, orgIds);
  }

  // Other app contexts not yet implemented — return empty until schemas land.
  return [];
}

/**
 * Organisations for which the user is an active **account manager** — the only
 * role permitted to access the billing portal. Use this (not
 * getUserOrganizations) anywhere billing data is exposed, so a user who is, say,
 * a lead facilitator of an org never sees that org's subscription or invoices.
 */
export async function getUserBillingOrganizations(
  client: SupabaseClient<Database>,
  userId: string
): Promise<Organization[]> {
  const orgIds = await getActiveMembershipOrgIds(client, userId, BILLING_ROLE);
  return fetchOrganizationsByIds(client, orgIds);
}

export async function getOrganizationById(
  client: SupabaseClient<Database>,
  organizationId: string
): Promise<Organization | null> {
  const { data, error } = await orgs(client)
    .select(ORGANIZATION_COLUMNS)
    .eq("id", organizationId)
    .single();

  if (error) {
    // PGRST116 = PostgREST "not found" error - this is expected
    if (error.code === "PGRST116") {
      return null;
    }
    throw new Error(`Failed to fetch organization: ${error.message}`);
  }

  return data as Organization;
}

export async function createOrganization(
  client: SupabaseClient<Database>,
  organization: Omit<Organization, "id" | "created_at" | "updated_at">
): Promise<Organization> {
  const { data, error } = await orgs(client)
    .insert(organization)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create organization: ${error.message}`);
  }

  return data as Organization;
}

export async function updateOrganization(
  client: SupabaseClient<Database>,
  organizationId: string,
  updates: Partial<Omit<Organization, "id" | "created_at" | "updated_at">>
): Promise<Organization> {
  const { data, error } = await orgs(client)
    .update(updates)
    .eq("id", organizationId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update organization: ${error.message}`);
  }

  return data as Organization;
}
